import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { useAuth } from '../../hooks/use-auth';
import * as badgesService from '../../services/api/badges';
import { Badge } from '../../entities/badge';
import { Language } from '../../entities/language';
import CommonAnimatedLoader from '../../components/common/animated/loader';
import AlertPrimary from '../../components/common/alerts/alert-primary';
import { strings } from '../../localization/strings';
import { Pagination } from '../../entities/pagination';
import Heading1 from '../../components/common/headings/heading1';
import CommonButtonDefault from '../../components/common/buttons/default';
import BadgesTableComponent from '../../components/badges/table';
import * as languagesService from '../../services/api/languages';

function BadgesListPageComponent() {
  const [queryParams, setQueryParams] = useSearchParams();
  const { token } = useAuth();
  const [pagination, setPagination] = useState<Pagination>();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>();
  const [badges, setBadges] = useState<Badge[]>([]);
  const [languages, setLanguages] = useState<Language[]>([]);
  const [page, setPage] = useState<number>(0);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const [items, activeLanguages] = await Promise.all([
          badgesService.list(token, { page, sort: 'id' }),
          languagesService.list(token),
        ]);

        setBadges(items.items);
        setLanguages(activeLanguages);
        setPagination(items.pagination);
        setIsLoading(false);
      } catch (e) {
        setIsLoading(false);
        setError((e as Error).message);
      }
    };

    fetchData();
  }, [page, token]);

  const updatePage = (currentPage: number) => {
    setPage(currentPage);
    setQueryParams({ page: currentPage.toString() });
  };

  return (
    <div>
      <header className="mb-3">
        <div className="flex justify-between space-x-3">
          <Heading1>{strings.badges.title}</Heading1>
          <Link to="/badges/create">
            <CommonButtonDefault type="button" primary>
              {strings.button.create}
            </CommonButtonDefault>
          </Link>
        </div>
      </header>
      <main className="w-full">
        <div className="overflow-hidden rounded-lg border border-gray-200 sm:rounded-lg">
          {queryParams.get('success') === '1' && (
            <div className="mx-auto my-5 max-w-7xl px-4 sm:px-6 lg:px-8">
              <AlertPrimary size="xs" type="success" text={strings.badges.messages.saved} />
            </div>
          )}
          {error && (
            <div className="mx-auto my-5 max-w-7xl px-4 sm:px-6 lg:px-8">
              <AlertPrimary type="danger" text={error} />
            </div>
          )}
          {isLoading ? (
            <CommonAnimatedLoader />
          ) : (
            <>
              {badges.length > 0 && pagination && (
                <BadgesTableComponent
                  badges={badges}
                  languages={languages}
                  pagination={pagination}
                  onUpdate={updatePage}
                />
              )}
              {badges.length === 0 && (
                <div className="mx-auto max-w-7xl p-4 text-xl sm:p-6 lg:p-8">{strings.search.noResults}</div>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
}

export default BadgesListPageComponent;
